import { computed, inject, Injectable } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { lastValueFrom, map } from 'rxjs';

import { MassAssignment } from '@/api/models/mass-assignment';
import { MassTicketStatusUpdate } from '@/api/models/mass-ticket-status-update';
import { TicketService } from '@/api/services';
import { TicketsScreenAdvancedFilters } from '@/screens/tickets/tickets.component';
import { AdvancedFiltersService } from '@/shared/services/advanced-filters.service';
import { FilterConfig, FilterType, ListService } from '@/shared/services/list.service';
import { MassAssigmentService } from '@/shared/services/mass-assigment.service';
import { toInfiniteQuery } from '@/shared/utils/infinitequery';

@Injectable({
  providedIn: 'any'
})
export class TicketsListService extends ListService {
  private apiService = inject(TicketService);
  private advancedFiltersService = inject(AdvancedFiltersService<TicketsScreenAdvancedFilters>);
  private ticketService = inject(TicketService);
  private massAssigmentService = inject(MassAssigmentService);

  private readonly filterConfig: FilterConfig = {
    // assigned_to_me: {
    //   value: `{"field":"ticket.assigned_user_id","operator":"equal","value":${this.logged_in_user_id}}`,
    //   type: FilterType.AND
    // },
    assigned_tickets: {
      value: `{"field":"ticket.assigned_user_id","operator":"is_not_null","value":""}`,
      type: FilterType.OR
    },
    unassigned: {
      value: `{"field":"ticket.assigned_user_id","operator":"is_null","value":""}`,
      type: FilterType.OR
    },
    is_online: {
      value: `{"field":"student.is_online","operator":"equal","value":true}`,
      type: FilterType.AND
    }
  };

  searchArray = computed(() => {
    if (!this.search()) return [];

    return [
      `{"field":"student.full_name","operator":"contains_ai_ci","value":"${this.search()}"}`,
      `{"field":"ticket.subject","operator":"contains_ai_ci","value":"${this.search()}"}`,
      `{"field":"course.name","operator":"contains_ai_ci","value":"${this.search()}"}`,
      `{"field":"student.email","operator":"contains_ai_ci","value":"${this.search()}"}`,
      `{"field":"ticket.id","operator":"contains_ai_ci","value":"${this.search()}"}`
    ];
  });

  readonly query = injectQuery(() => ({
    queryKey: [
      'tickets',
      this.pageParam(),
      this.limit(),
      this.sortingString(),
      this.searchArray(),
      this.filtersString(),
      this.advancedFiltersService.advancedFilters()
    ] as const,
    enabled: this.isQueryActive(),
    queryFn: async ({ queryKey }) => {
      const page = queryKey[1];
      const limit = queryKey[2];
      const sort = queryKey[3];
      const advancedFilters = queryKey[6] as TicketsScreenAdvancedFilters;

      // Add dynamic advanced filters
      const dynamicFilters = this.createDynamicFilters(advancedFilters);

      const filters = this.buildFilters(this.searchArray(), queryKey[5] as string, dynamicFilters, advancedFilters);

      const params = this.getQueryParams({ page, limit, sort, filters });

      return lastValueFrom(this.apiService.apiTicketOverviewGet(params).pipe(toInfiniteQuery(params)));
    }
  }));

  readonly countQuery = injectQuery(() => ({
    queryKey: [
      'tickets-count',
      this.searchArray(),
      this.filtersString(),
      this.advancedFiltersService.advancedFilters()
    ],
    enabled: this.isQueryActive(),
    queryFn: async ({ queryKey }) => {
      const advancedFilters = queryKey[3] as TicketsScreenAdvancedFilters;

      // Add dynamic advanced filters
      const dynamicFilters = this.createDynamicFilters(advancedFilters);

      const filters = this.buildFilters(this.searchArray(), queryKey[2] as string, dynamicFilters, advancedFilters);

      const params = this.getQueryParams({ page: undefined, limit: undefined, sort: undefined, filters });

      return lastValueFrom(this.apiService.apiTicketOnlineGet(params).pipe(map((res) => res.count)));
    }
  }));

  async doMassAssign(ticketIdList: number[], assignedUserId: number | null): Promise<void> {
    const page = this.pageParam();
    const sort = this.sortingString();
    const advancedFilters = this.advancedFiltersService.advancedFilters() as TicketsScreenAdvancedFilters;

    // Add dynamic advanced filters
    const dynamicFilters = this.createDynamicFilters(advancedFilters);

    const filters = this.buildFilters(
      this.searchArray(),
      this.filtersString() as string,
      dynamicFilters,
      advancedFilters
    );

    const queryParams = this.getQueryParams({ page, sort, filters });

    const body: MassAssignment = {
      tickets_ids: ticketIdList,
      assigned_user_id: assignedUserId,
      is_exclusion_mode: this.massAssigmentService.exclusionMode()
    };

    const params = {
      body,
      ...(this.massAssigmentService.exclusionMode() ? queryParams : {})
    };

    await lastValueFrom(this.ticketService.apiTicketMassAssignmentPut(params));
  }

  async doMassChangeStatus(ticketIdList: number[], status: string): Promise<void> {
    const page = this.pageParam();
    const sort = this.sortingString();
    const advancedFilters = this.advancedFiltersService.advancedFilters() as TicketsScreenAdvancedFilters;

    // Add dynamic advanced filters
    const dynamicFilters = this.createDynamicFilters(advancedFilters);

    const filters = this.buildFilters(
      this.searchArray(),
      this.filtersString() as string,
      dynamicFilters,
      advancedFilters
    );

    const queryParams = this.getQueryParams({ page, sort, filters });

    const body: MassTicketStatusUpdate = {
      items_ids: ticketIdList,
      status: status,
      is_exclusion_mode: this.massAssigmentService.exclusionMode()
    };

    const params = {
      body,
      ...(this.massAssigmentService.exclusionMode() ? queryParams : {})
    };

    await lastValueFrom(this.ticketService.apiTicketMassStatusPut(params));
  }

  getTicketsCurrentFilters(): Record<string, unknown> {
    const page = this.pageParam();
    const sort = this.sortingString();
    const advancedFilters = this.advancedFiltersService.advancedFilters() as TicketsScreenAdvancedFilters;

    // Add dynamic advanced filters
    const dynamicFilters = this.createDynamicFilters(advancedFilters);

    const filters = this.buildFilters(
      this.searchArray(),
      this.filtersString() as string,
      dynamicFilters,
      advancedFilters
    );

    return this.getQueryParams({ page, sort, filters });
  }

  assignedToDynamicFilter(assignedTo: string[]): string {
    return assignedTo[0] === 'Unassigned'
      ? this.filterConfig['unassigned'].value
      : `{"field":"ticket.assigned_user_id","operator":"equal","value":${assignedTo}}`;
  }

  private createDynamicFilters(advancedFilters: TicketsScreenAdvancedFilters): FilterConfig {
    const formatDateForRange = (date: Date | string | null, isEnd = false): string | null => {
      if (!date) return null;

      const actualDate = typeof date === 'string' ? new Date(date) : date;

      const year = actualDate.getFullYear();
      const month = String(actualDate.getMonth() + 1).padStart(2, '0');
      const day = String(actualDate.getDate()).padStart(2, '0');
      const hour = isEnd ? '23' : '00';
      const minute = isEnd ? '59' : '00';
      const second = isEnd ? '59' : '00';

      return `${year}-${month}-${day} ${hour}:${minute}:${second}+00`;
    };

    const test = {
      ...this.filterConfig,
      ...(advancedFilters.region && {
        region: {
          value: `{"field":"region.id","operator":"in","value":${JSON.stringify(advancedFilters.region)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.countries && {
        countries: {
          value: `{"field":"country.id","operator":"in","value":${JSON.stringify(advancedFilters.countries)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.educational_level && {
        educational_level: {
          value: `{"field":"educational_level.id","operator":"in","value":${JSON.stringify(advancedFilters.educational_level)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.category && {
        category: {
          value: `{"field":"category.id","operator":"in","value":${JSON.stringify(advancedFilters.category)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.student?.[0] && {
        student: {
          value: `{"field":"student.id","operator":"equal","value":${JSON.stringify(advancedFilters.student[0])}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.enrolmentStatus && {
        enrolmentStatus: {
          value: `{"field":"enrolment.status","operator":"in","value":${JSON.stringify(advancedFilters.enrolmentStatus)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.status && {
        status: {
          value: `{"field":"ticket.status","operator":"in","value":${JSON.stringify(advancedFilters.status)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.assignedTo && {
        assignedTo: {
          value: this.assignedToDynamicFilter(advancedFilters.assignedTo),
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.institution && {
        institution: {
          value: `{"field":"institution.id","operator":"in","value":${JSON.stringify(advancedFilters.institution)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.double_institution && {
        double_institution: {
          value: `{"field":"institution.id","operator":"in","value":${JSON.stringify(advancedFilters.double_institution)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.area && {
        area: {
          value: `{"field":"area.id","operator":"in","value":${JSON.stringify(advancedFilters.area)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.faculty && {
        faculty: {
          value: `{"field":"faculty.id","operator":"in","value":${JSON.stringify(advancedFilters.faculty)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.school && {
        school: {
          value: `{"field":"school.id","operator":"in","value":${JSON.stringify(advancedFilters.school)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.group && {
        group: {
          value: `{"field":"group.id","operator":"in","value":${JSON.stringify(advancedFilters.group)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.course && {
        course: {
          value: `{"field":"course.id","operator":"in","value":${JSON.stringify(advancedFilters.course)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.merchant && {
        merchant: {
          value: `{"field":"merchant.id","operator":"in","value":${JSON.stringify(advancedFilters.merchant)}}`,
          type: FilterType.AND
        }
      }),
      ...(advancedFilters.ticketStartDate &&
        (advancedFilters.ticketStartDate.start || advancedFilters.ticketStartDate.end) && {
          ticketStartDate: {
            value: advancedFilters.ticketStartDate.end
              ? `{"field":"ticket.created_at","operator":"between","value":[${JSON.stringify(formatDateForRange(advancedFilters.ticketStartDate.start))},${JSON.stringify(formatDateForRange(advancedFilters.ticketStartDate.end, true))}]}`
              : `{"field":"ticket.created_at","operator":"between","value":[${JSON.stringify(formatDateForRange(advancedFilters.ticketStartDate.start))},${JSON.stringify(formatDateForRange(advancedFilters.ticketStartDate.start, true))}]}`,
            type: FilterType.AND
          }
        }),
      ...(advancedFilters.ticketModDate &&
        (advancedFilters.ticketModDate.start || advancedFilters.ticketModDate.end) && {
          ticketModDate: {
            value: advancedFilters.ticketModDate.end
              ? `{"field":"ticket.modified_at","operator":"between","value":[${JSON.stringify(formatDateForRange(advancedFilters.ticketModDate.start))},${JSON.stringify(formatDateForRange(advancedFilters.ticketModDate.end, true))}]}`
              : `{"field":"ticket.modified_at","operator":"between","value":[${JSON.stringify(formatDateForRange(advancedFilters.ticketModDate.start))},${JSON.stringify(formatDateForRange(advancedFilters.ticketModDate.start, true))}]}`,
            type: FilterType.AND
          }
        })
    };

    return test;
  }
}
